/**
 * Centralized HTTP helpers for removal-order flow. Import from here instead of
 * inline fetch in removal pages/components.
 */

import {
  resolveSignageSessionOrganizationId,
  appendPermissionsFalse,
  isSalesOrderSavedAddress,
  resolveSalesOrderAddressId,
  mergeSalesOrderSavedAddresses,
  buildSalesAddressSelectOptions,
  sanitizeOrganizationId,
  isSignageSellerLicense,
} from "./salesOrderApi";
import { getAuthCookie } from "../utils/authCookies";

export const REMOVAL_API_BASE = process.env.REACT_APP_API_URL || "";

export type RemovalFetchOptions = {
  signal?: AbortSignal;
  raw?: boolean;
};

function authHeaders(token: string | null | undefined, json = true): HeadersInit {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (json) headers["Content-Type"] = "application/json";
  return headers;
}

async function parseJsonSafe(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

async function removalFetch(
  path: string,
  token: string | null | undefined,
  init: RequestInit = {},
  options?: RemovalFetchOptions
): Promise<Response> {
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }
  return fetch(`${REMOVAL_API_BASE}${path}`, {
    ...init,
    headers,
    signal: options?.signal ?? init.signal,
  });
}

export async function removalRequest(
  path: string,
  token: string | null | undefined,
  init: RequestInit = {},
  options?: RemovalFetchOptions
): Promise<Response> {
  return removalFetch(path, token, init, options);
}

async function removalJson<T = any>(
  path: string,
  token: string | null | undefined,
  init: RequestInit = {},
  options?: RemovalFetchOptions
): Promise<T> {
  const res = await removalFetch(path, token, init, options);
  const data = await parseJsonSafe(res);
  if (!res.ok) {
    const message =
      (data as { message?: string })?.message ||
      (data as { error?: string })?.error ||
      `Request failed (${res.status})`;
    throw new Error(String(message));
  }
  return data as T;
}

// ─── URL builders ───────────────────────────────────────────────────────────

export const removalOrderUrls = {
  orders: () => `/removal_orders`,
  order: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}`,
  /** POST — seller signage organization id (was signage_id / membership id). */
  dynamicCost: (organizationId: string) =>
    `/removal_orders/dynamic_cost/${encodeURIComponent(organizationId)}`,
  orgDatatable: (organizationId: string) =>
    `/removal_orders/organizations/${encodeURIComponent(organizationId)}/datatable`,
  orgOrders: (organizationId: string) =>
    `/removal_orders/organizations/${encodeURIComponent(organizationId)}`,
  createdBy: () => `/removal_orders/created_by`,
  createdByDatatable: () => `/removal_orders/created_by/datatable`,
  /** GET — installer membership id (was user_id). */
  scheduledJobs: (membershipId: string, date: string) =>
    `/removal_orders/${encodeURIComponent(membershipId)}/${encodeURIComponent(date)}/scheduled_jobs`,
  finalize: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/finalize`,
  schedule: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/schedule`,
  reschedule: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/reschedule`,
  start: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/start`,
  complete: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/complete`,
  closed: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/closed`,
  cancel: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/cancel`,
  void: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/void`,
  payment: (orderId: string) => `/removal_orders/${encodeURIComponent(orderId)}/payment`,
  net30Payment: (orderId: string) =>
    `/removal_orders/${encodeURIComponent(orderId)}/net30_payment`,
  uploadProof: (orderId: string) =>
    `/removal_orders/${encodeURIComponent(orderId)}/upload-proof`,
  trackingSummary: (installationOrderId: string) =>
    `/removal_tracking/installation_order/${encodeURIComponent(installationOrderId)}/summary`,
  finalSubmittedItems: (orderId: string) =>
    `/removal-orders/${encodeURIComponent(orderId)}/final-submitted-items`,
  publicOrder: (orderId: string) => `/public/removal_orders/${encodeURIComponent(orderId)}`,
  publicPayment: (orderId: string, query = "") =>
    `/public/removal_orders/${encodeURIComponent(orderId)}/payment${query ? `?${query}` : ""}`,
  publicFinalize: (orderId: string) =>
    `/public/removal_orders/${encodeURIComponent(orderId)}/finalize`,
  paymentGateway: (organizationId: string) =>
    `/payments/gateway/organizations/${encodeURIComponent(organizationId)}`,
  stripePaymentIntent: (organizationId: string) =>
    `/payments/stripe/organizations/${encodeURIComponent(organizationId)}/payment-intent`,
  paymentMethods: (organizationId: string, membershipId: string) =>
    `/payment-methods/organizations/${encodeURIComponent(organizationId)}/memberships/${encodeURIComponent(membershipId)}`,
};

export type RemovalOrderIdentity = {
  inventory_source?: string;
  signage_id?: string;
  seller_org_id?: string;
  seller_org_type?: string;
  seller_owner_node_id?: string;
  buyer_org_id?: string;
  source_org_id?: string;
  agent_org_id?: string;
  team_member_org_id?: string;
  brokerage_firm_org_id?: string;
  signage_company_name?: string;
  agent_id?: string;
  agent_membership_id?: string;
  team_member_id?: string;
  team_member_membership_id?: string;
  buyer_membership_id?: string;
  source_membership_id?: string;
  installation_details?: {
    installer_id?: string;
    installer_membership_id?: string;
    installer_name?: string;
    contact_number?: string;
  };
  removal_details?: {
    installer_org_id?: string;
    installer_membership_id?: string;
    installer_id?: string;
    installer_name?: string;
    contact_number?: string;
  };
  billing_address?: { _id?: string; address_details?: { _id?: string } };
};

/** Buyer-side org ids that must never be used as the signage seller org for payments. */
function removalBuyerSideOrganizationIds(order?: RemovalOrderIdentity | null): Set<string> {
  const ids = [
    order?.buyer_org_id,
    order?.source_org_id,
    order?.agent_org_id,
    order?.team_member_org_id,
    order?.brokerage_firm_org_id,
    getAuthCookie("organizationId"),
  ]
    .map(sanitizeOrganizationId)
    .filter(Boolean);
  return new Set(ids);
}

function isRemovalSignageSellerOrgType(orgType?: string | null): boolean {
  const t = String(orgType ?? "").trim().toUpperCase();
  return t === "SIGNAGE_FIRM" || t === "SIGNAGE" || t.startsWith("SIGNAGE_");
}

/**
 * Signage seller organization id for removal payment routes (gateway, Stripe, payment methods).
 * On agent/realtor logins, never returns the buyer/session organization id.
 */
export function resolveRemovalPaymentSellerOrganizationId(
  order?: RemovalOrderIdentity | null,
  legacySignageIdFallback?: string | null
): string {
  if (isSignageSellerLicense()) {
    return resolveSignageSessionOrganizationId();
  }

  const buyerOrgIds = removalBuyerSideOrganizationIds(order);
  const storedSupplierOrg =
    typeof localStorage !== "undefined"
      ? sanitizeOrganizationId(localStorage.getItem("supplierOrganizationId"))
      : "";

  const candidates: (string | null | undefined)[] = [
    legacySignageIdFallback ?? order?.signage_id,
    order?.seller_owner_node_id,
    order?.removal_details?.installer_org_id,
    storedSupplierOrg,
  ];

  const sellerOrgId = sanitizeOrganizationId(order?.seller_org_id);
  const sellerOrgType = String(order?.seller_org_type ?? "").trim();
  if (
    sellerOrgId &&
    (isRemovalSignageSellerOrgType(sellerOrgType) || !buyerOrgIds.has(sellerOrgId))
  ) {
    candidates.push(sellerOrgId);
  }

  for (const raw of candidates) {
    const id = sanitizeOrganizationId(raw);
    if (!id || buyerOrgIds.has(id)) continue;
    return id;
  }

  return "";
}

/** Resolve seller signage organization id for org-scoped removal routes. */
export function resolveRemovalSellerOrganizationId(
  fallbackSignageOrOrgId?: string | null,
  order?: RemovalOrderIdentity | null
): string {
  return resolveRemovalPaymentSellerOrganizationId(order, fallbackSignageOrOrgId);
}

/** Logged-in signage org from session cookie. */
export function resolveRemovalSessionSellerOrganizationId(): string {
  return resolveSignageSessionOrganizationId();
}

export function resolveRemovalAgentMembershipId(
  order?: RemovalOrderIdentity | null
): string {
  if (!order) return "";
  return String(
    order.agent_membership_id ?? order.agent_id ?? order.buyer_membership_id ?? ""
  ).trim();
}

export function resolveRemovalTeamMemberMembershipId(
  order?: RemovalOrderIdentity | null
): string {
  if (!order) return "";
  return String(
    order.team_member_membership_id ?? order.team_member_id ?? ""
  ).trim();
}

export function resolveRemovalAddressMembershipId(
  order?: RemovalOrderIdentity | null
): string {
  if (!order) return "";
  const isOffice =
    String(order.inventory_source || "").trim().toLowerCase() === "office";
  if (isOffice) {
    const teamMember = resolveRemovalTeamMemberMembershipId(order);
    if (teamMember) return teamMember;
  }
  const agent = resolveRemovalAgentMembershipId(order);
  if (agent) return agent;
  return String(order.buyer_membership_id ?? order.source_membership_id ?? "").trim();
}

export function resolveRemovalPaymentMethodsMembershipId(
  order?: RemovalOrderIdentity | null,
  sessionMembershipIdFallback?: string | null
): string {
  const sessionMembershipId = String(
    sessionMembershipIdFallback ?? getAuthCookie("membershipId") ?? ""
  ).trim();
  if (!order) return sessionMembershipId;
  const preferred = String(
    order.buyer_membership_id ??
      order.agent_membership_id ??
      order.team_member_membership_id ??
      order.source_membership_id ??
      order.agent_id ??
      order.team_member_id ??
      ""
  ).trim();
  return preferred || sessionMembershipId;
}

export type RemovalInstallerPicker = {
  value?: string;
  label?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
};

/** Read assigned installer from saved removal/installation order details. */
export function removalInstallerPickerFromOrder(
  order?: RemovalOrderIdentity | null
): RemovalInstallerPicker | null {
  const details = order?.removal_details;
  const installation = order?.installation_details;
  const membershipId = String(
    details?.installer_membership_id ??
      details?.installer_id ??
      installation?.installer_membership_id ??
      installation?.installer_id ??
      ""
  ).trim();
  const name = String(
    details?.installer_name ?? installation?.installer_name ?? ""
  ).trim();
  if (!membershipId && !name) return null;
  const parts = name.split(/\s+/).filter(Boolean);
  return {
    value: membershipId,
    label: name || membershipId,
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ") || "",
    phone: String(details?.contact_number ?? installation?.contact_number ?? "").trim(),
    email: "",
  };
}

export function resolveRemovalInstallerMembershipId(
  order?: RemovalOrderIdentity | null,
  selected?: RemovalInstallerPicker | null
): string {
  return String(
    selected?.value ?? removalInstallerPickerFromOrder(order)?.value ?? ""
  ).trim();
}

export function resolveRemovalInstallerName(
  order?: RemovalOrderIdentity | null,
  selected?: RemovalInstallerPicker | null
): string {
  return String(
    selected?.label ?? removalInstallerPickerFromOrder(order)?.label ?? ""
  ).trim();
}

/** Installer fields for POST/PUT removal_orders payloads (uses foundation installer_membership_id). */
export function buildRemovalInstallerPayloadFields(
  order?: RemovalOrderIdentity | null,
  selected?: RemovalInstallerPicker | null,
  extras?: { contactNumber?: string | null }
): Record<string, string> {
  const installerMembershipId = resolveRemovalInstallerMembershipId(order, selected);
  const installerName = resolveRemovalInstallerName(order, selected);
  const contactNumber = String(
    extras?.contactNumber ??
      selected?.phone ??
      order?.removal_details?.contact_number ??
      order?.installation_details?.contact_number ??
      ""
  ).trim();

  const out: Record<string, string> = {};
  if (installerMembershipId) {
    out.installer_membership_id = installerMembershipId;
    out.assignee_membership_id = installerMembershipId;
    out.installer_id = installerMembershipId;
  }
  if (installerName) out.installer_name = installerName;
  if (contactNumber) out.contact_number = contactNumber;
  return out;
}

/** Buyer membership id for special-fee catalog fetch on removal screens. */
export function resolveRemovalSpecialFeePersonId(
  order?: RemovalOrderIdentity | null,
  options?: {
    selectedPersonId?: string | null;
    sessionMembershipId?: string | null;
  }
): string {
  const fromOrder = resolveRemovalPaymentMethodsMembershipId(
    order,
    options?.sessionMembershipId
  );
  if (fromOrder) return fromOrder;
  return String(options?.selectedPersonId ?? "").trim();
}

export function buildRemovalDynamicCostAddressBody(addressDetails?: {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  cityCode?: string;
  stateCode?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
} | null) {
  const d = addressDetails || {};
  return {
    removal_address: {
      addressLine1: d.addressLine1 ?? "",
      addressLine2: d.addressLine2 ?? "",
      city: d.city ?? "",
      state: d.state ?? "",
      country: d.country ?? "",
      zip: d.zip ?? "",
      cityCode: d.cityCode ?? "",
      stateCode: d.stateCode ?? "",
      countryCode: d.countryCode ?? "",
      latitude: d.latitude ?? 0,
      longitude: d.longitude ?? 0,
    },
  };
}

/** Resolve react-select / form value id for a saved removal order billing address. */
export function resolveRemovalBillingAddressId(
  address?: { _id?: string; address_details?: { _id?: string } } | null
): string {
  return resolveSalesOrderAddressId(address);
}

export function findRemovalAddressById<
  T extends { _id?: string; address_details?: { _id?: string } },
>(addresses: T[] | null | undefined, id?: string | null): T | undefined {
  const needle = String(id ?? "").trim();
  if (!needle) return undefined;
  return addresses?.find(
    (addr) =>
      String(addr._id ?? "") === needle ||
      String(addr.address_details?._id ?? "") === needle
  );
}

export function mergeRemovalOrderBillingAddress<
  T extends { _id?: string; address_details?: { _id?: string } },
>(addressObjects: T[], billingAddress?: T | null): T[] {
  return mergeSalesOrderSavedAddresses(addressObjects, billingAddress);
}

/** Build react-select option for a saved billing address row. */
export function removalBillingAddressSelectOption(address: {
  _id?: string;
  label?: string;
  address_details?: {
    addressLine1?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}) {
  const d = address.address_details;
  const value = resolveSalesOrderAddressId(address) || address._id;
  return {
    ...address,
    value,
    label: `${address.label ?? ""}: ${d?.addressLine1 ?? ""}, ${d?.city ?? ""}, ${d?.state ?? ""}, ${d?.zip ?? ""}`,
  };
}

export function buildRemovalAddressOptions(
  addressObjects: any[] | null | undefined,
  billingAddress?: any | null
) {
  return buildSalesAddressSelectOptions(
    addressObjects,
    billingAddress ? [billingAddress] : undefined
  );
}

export type ResolveRemovalSubmitBillingInput = {
  billingAddressId?: string | null;
  inventorySource?: string | null;
  addressObjects?: Array<{ _id?: string; address_details?: { _id?: string } }> | null;
  selectedBillingAddress?: unknown;
  selectedOfficeBillingAddress?: unknown;
  orderBillingAddress?: unknown;
  formBillingAddress?: unknown;
  bothAddressSame?: boolean;
  removalAddress?: unknown;
};

/** Resolve full billing address object for removal POST / resolve (not just react state). */
export function resolveRemovalSubmitBillingAddress(
  input: ResolveRemovalSubmitBillingInput
): unknown | null {
  const billingId = String(input.billingAddressId ?? "").trim();
  const addresses = input.addressObjects ?? [];

  if (billingId && addresses.length > 0) {
    const fromList = findRemovalAddressById(addresses, billingId);
    if (fromList) return fromList;
  }

  const isOffice = String(input.inventorySource ?? "").trim().toLowerCase() === "office";
  const selected = isOffice
    ? input.selectedOfficeBillingAddress ?? input.selectedBillingAddress
    : input.selectedBillingAddress ?? input.selectedOfficeBillingAddress;

  if (selected && typeof selected === "object" && isSalesOrderSavedAddress(selected as any)) {
    return selected;
  }

  const orderBilling = input.orderBillingAddress;
  if (orderBilling && typeof orderBilling === "object" && isSalesOrderSavedAddress(orderBilling as any)) {
    const orderId = resolveSalesOrderAddressId(orderBilling as any);
    if (!billingId || orderId === billingId) {
      return orderBilling;
    }
  }

  const formBilling = input.formBillingAddress;
  if (formBilling && typeof formBilling === "object" && isSalesOrderSavedAddress(formBilling as any)) {
    return formBilling;
  }

  if (input.bothAddressSame) {
    const removal = input.removalAddress;
    if (removal && typeof removal === "object" && isSalesOrderSavedAddress(removal as any)) {
      return removal;
    }
  }

  return null;
}

export function unwrapRemovalOrderPayload(raw: unknown): any {
  if (!raw || typeof raw !== "object") return raw;
  const r = raw as Record<string, unknown>;
  const data = r.data;
  if (
    data &&
    typeof data === "object" &&
    !Array.isArray(data) &&
    ((data as Record<string, unknown>)._id ||
      (data as Record<string, unknown>).id ||
      (data as Record<string, unknown>).order_number)
  ) {
    return data;
  }
  return r;
}

// ─── Removal orders ─────────────────────────────────────────────────────────

export async function createRemovalOrder(
  token: string | null | undefined,
  body: unknown,
  options?: RemovalFetchOptions
): Promise<Response> {
  return removalRequest(
    removalOrderUrls.orders(),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function fetchRemovalOrderById(
  token: string | null | undefined,
  orderId: string,
  options?: RemovalFetchOptions
) {
  return removalJson(
    removalOrderUrls.order(orderId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchRemovalOrderByIdUnwrapped(
  token: string | null | undefined,
  orderId: string,
  options?: RemovalFetchOptions
) {
  const raw = await fetchRemovalOrderById(token, orderId, options);
  return unwrapRemovalOrderPayload(raw);
}

export async function patchRemovalOrder(
  token: string | null | undefined,
  orderId: string,
  body: unknown,
  options?: RemovalFetchOptions
): Promise<Response> {
  return removalRequest(
    removalOrderUrls.order(orderId),
    token,
    { method: "PATCH", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function postRemovalDynamicCost(
  token: string | null | undefined,
  organizationId: string,
  body: unknown,
  options?: RemovalFetchOptions
) {
  const orgId = String(organizationId || "").trim();
  if (!orgId) {
    throw new Error("organization_id is required for removal dynamic cost");
  }
  return removalJson(
    removalOrderUrls.dynamicCost(orgId),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export type RemovalOrderAction =
  | "finalize"
  | "schedule"
  | "reschedule"
  | "start"
  | "complete"
  | "closed"
  | "cancel"
  | "void";

export async function postRemovalOrderAction(
  token: string | null | undefined,
  orderId: string,
  action: RemovalOrderAction,
  body?: unknown,
  options?: RemovalFetchOptions
): Promise<Response> {
  const pathByAction: Record<RemovalOrderAction, string> = {
    finalize: removalOrderUrls.finalize(orderId),
    schedule: removalOrderUrls.schedule(orderId),
    reschedule: removalOrderUrls.reschedule(orderId),
    start: removalOrderUrls.start(orderId),
    complete: removalOrderUrls.complete(orderId),
    closed: removalOrderUrls.closed(orderId),
    cancel: removalOrderUrls.cancel(orderId),
    void: removalOrderUrls.void(orderId),
  };
  return removalRequest(
    pathByAction[action],
    token,
    {
      method: "POST",
      headers: authHeaders(token),
      ...(body != null ? { body: JSON.stringify(body) } : {}),
    },
    options
  );
}

export async function postRemovalOrderPayment(
  token: string | null | undefined,
  orderId: string,
  body: unknown,
  method: "POST" | "PUT" = "POST",
  options?: RemovalFetchOptions
) {
  return removalJson(
    removalOrderUrls.payment(orderId),
    token,
    { method, headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function postRemovalOrderNet30Payment(
  token: string | null | undefined,
  orderId: string,
  body: unknown,
  options?: RemovalFetchOptions
) {
  return removalJson(
    removalOrderUrls.net30Payment(orderId),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function putRemovalOrderUploadProof(
  token: string | null | undefined,
  orderId: string,
  body: FormData,
  options?: RemovalFetchOptions
): Promise<Response> {
  return removalRequest(
    removalOrderUrls.uploadProof(orderId),
    token,
    { method: "POST", headers: { Authorization: `Bearer ${token}` }, body },
    options
  );
}

export async function fetchRemovalPaymentGateway(
  token: string | null | undefined,
  organizationId: string,
  options?: RemovalFetchOptions
) {
  return removalJson(
    appendPermissionsFalse(removalOrderUrls.paymentGateway(organizationId)),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchRemovalPaymentMethods(
  token: string | null | undefined,
  organizationId: string,
  membershipId: string,
  options?: RemovalFetchOptions
) {
  const orgId = String(organizationId || "").trim();
  const memId = String(membershipId || "").trim();
  if (!orgId || !memId) return [];
  return removalJson(
    removalOrderUrls.paymentMethods(orgId, memId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function createRemovalStripePaymentIntent(
  token: string | null | undefined,
  organizationId: string,
  body: unknown,
  options?: RemovalFetchOptions
) {
  const amountCents = Number((body as { amount?: unknown } | null)?.amount ?? 0);
  if (!(amountCents > 0)) {
    return { skipped: true, clientSecret: null, client_secret: null };
  }
  return removalJson(
    removalOrderUrls.stripePaymentIntent(organizationId),
    token,
    { method: "POST", headers: authHeaders(token), body: JSON.stringify(body) },
    options
  );
}

export async function fetchRemovalScheduledJobs(
  token: string | null | undefined,
  membershipId: string,
  date: string,
  options?: RemovalFetchOptions
) {
  return removalJson(
    removalOrderUrls.scheduledJobs(membershipId, date),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

export async function fetchRemovalTrackingSummary(
  token: string | null | undefined,
  installationOrderId: string,
  options?: RemovalFetchOptions
) {
  return removalJson(
    removalOrderUrls.trackingSummary(installationOrderId),
    token,
    { method: "GET", headers: authHeaders(token) },
    options
  );
}

/** Contact shape compatible with legacy notification/email helpers. */
export type RemovalMembershipContact = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  licenseType?: string;
  companyName?: string;
  firmDetails?: { companyName?: string };
  [key: string]: unknown;
};

function mapRemovalMembershipContactRow(
  row: Record<string, unknown> | null | undefined,
  fallbackId: string
): RemovalMembershipContact | null {
  if (!row) return null;
  const id = String(
    row.global_user_id ?? row.globalUserId ?? row.id ?? row._id ?? fallbackId
  ).trim();
  if (!id) return null;
  const firstName = String(row.firstName ?? row.first_name ?? "");
  const lastName = String(row.lastName ?? row.last_name ?? "");
  const companyName = String(row.company_name ?? row.companyName ?? "");
  return {
    id,
    email: String(row.email ?? ""),
    firstName,
    lastName,
    licenseType: String(row.licenseType ?? row.license_type ?? ""),
    companyName,
    firmDetails: companyName ? { companyName } : undefined,
    ...row,
  };
}

/** Fetch membership contact via foundation min-details (replaces GET /users/{id}). */
export async function fetchRemovalMembershipContact(
  token: string | null | undefined,
  membershipId: string | null | undefined
): Promise<RemovalMembershipContact | null> {
  const id = String(membershipId ?? "").trim();
  if (!id || !token) return null;
  try {
    const res = await removalFetch(
      `/organization-memberships/${encodeURIComponent(id)}/min-details`,
      token,
      { method: "GET", headers: authHeaders(token) }
    );
    if (!res.ok) return null;
    const data = await parseJsonSafe(res);
    const row = (data as { data?: Record<string, unknown> })?.data ?? data;
    return mapRemovalMembershipContactRow(row as Record<string, unknown>, id);
  } catch {
    return null;
  }
}

export async function fetchMultipleRemovalMembershipContacts(
  membershipIds: (string | null | undefined)[],
  token: string | null | undefined
): Promise<Map<string, RemovalMembershipContact>> {
  const map = new Map<string, RemovalMembershipContact>();
  if (!token) return map;
  const validIds = Array.from(
    new Set(membershipIds.map((mid) => String(mid ?? "").trim()).filter(Boolean))
  );
  await Promise.all(
    validIds.map(async (mid) => {
      const contact = await fetchRemovalMembershipContact(token, mid);
      if (contact) map.set(mid, contact);
    })
  );
  return map;
}

/** Session actor from auth cookies — avoids GET /users/{id} for the current user. */
export function removalSessionContactFromCookies(): RemovalMembershipContact | null {
  const membershipId = String(getAuthCookie("membershipId") ?? "").trim();
  const userId = String(getAuthCookie("userId") ?? "").trim();
  const id = userId || membershipId;
  if (!id) return null;
  const userName = String(getAuthCookie("userName") ?? "").trim();
  const parts = userName.split(/\s+/).filter(Boolean);
  return {
    id,
    email: String(getAuthCookie("email") ?? ""),
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
    licenseType: String(getAuthCookie("licenseType") ?? ""),
  };
}

export function resolveRemovalBrokerageMembershipId(
  order?: RemovalOrderIdentity | null
): string {
  if (!order) return "";
  return String(
    (order as { brokerage_firm_membership_id?: string }).brokerage_firm_membership_id ??
      order.buyer_membership_id ??
      order.team_member_membership_id ??
      (order as { brokerage_firm_id?: string }).brokerage_firm_id ??
      ""
  ).trim();
}

/** Signage notification target from order installer (external actor notifying signage). */
export function resolveRemovalSignageOrderNotificationTarget(
  order?: RemovalOrderIdentity | null
): { membershipId: string; licenseType: string } {
  const membershipId = String(
    resolveRemovalInstallerMembershipId(order) ||
      (order as { seller_owner_node_id?: string })?.seller_owner_node_id ||
      ""
  ).trim();
  return { membershipId, licenseType: "SIGNAGE_FIRM" };
}

/** Signage-side notification target from session or order installer. */
export function resolveRemovalSignageNotificationTarget(
  order?: RemovalOrderIdentity | null
): { membershipId: string; licenseType: string } {
  const sessionMembershipId = String(
    getAuthCookie("membershipId") || getAuthCookie("userId") || ""
  ).trim();
  const sessionLicense = String(getAuthCookie("licenseType") || "");
  if (
    sessionMembershipId &&
    (sessionLicense.startsWith("SIGNAGE") || !resolveRemovalInstallerMembershipId(order))
  ) {
    return { membershipId: sessionMembershipId, licenseType: sessionLicense || "SIGNAGE_FIRM" };
  }
  return resolveRemovalSignageOrderNotificationTarget(order);
}
